import { EWebclientScriptType } from '@constants/EWebclientScriptType';
import type {
  GetWebclientConfigDto,
  WebclientConfig,
} from '@conversed/shared/src/central/dto/webclient-config.dto';
import type { IWebclientConfigItem } from '@conversed/shared/src/central/types/webclient-config.type';
import {
  AnimationType,
  DEFAULT_CHAT_WINDOW_FONT_FAMILY,
  IconType,
} from '@conversed/shared/src/central/types/webclient-config.type';
import type { TWebclient } from '../interfaces/redux/TSettingsState';

// * Generic over the item shape: the preview endpoint sends a slimmer config
// * item than the settings endpoint, and both need the same default lookup.
export const findDefaultWebclientConfig = <
  TConfigItem extends Pick<IWebclientConfigItem, 'isDefault' | 'isEnabled'>,
>(
  configs: TConfigItem[],
): TConfigItem | undefined => {
  // * Prefer a config explicitly marked as default that is also enabled
  const explicitDefault = configs.find(
    ({ isDefault, isEnabled }) => isDefault === true && isEnabled !== false,
  );

  if (explicitDefault) {
    return explicitDefault;
  }

  // * Fall back to the first enabled config when no explicit default exists
  return configs.find(({ isEnabled }) => isEnabled !== false);
};

export const defaultWebclientConfig: WebclientConfig = {
  options: {
    useAttachment: false,
    menuTemplate: [],
    messagePlaceholder: 'Type here',
    welcomeMessage: 'Hi there! I am here to chat with you. Ready to start? 😊',
    showWelcomeAsFirstMessage: true,
    waitingForTheBotResponseMessage:
      'Please wait for the bot to finish responding.',
    enableWaitingForBotResponseBlocker: false,
    notificationAIGeneratedAnswer: 'AI antwoord',
    defaultState: 'greeting',
    headerType: 'normal',
    title: 'Conversed',
    subtitle: 'AI powered chatbot',
    refParam: 'normal',
    showCloseButton: false,
    popupTimeout: 0,
    greetingTimeout: 500,
    greeting: {
      buttonText: "Let's go!",
      messageIcon: '',
      allowLaterButton: true,
    },
    icons: {
      chatIcon: {
        open: 'https://s3.eu-central-1.amazonaws.com/webclient-connector-staging.conversed.biz/assets/opened.svg',
        closed: {
          type: IconType.STATIC,
          path: 'https://s3.eu-central-1.amazonaws.com/webclient-connector-staging.conversed.biz/assets/closed.svg',
          animation: AnimationType.NONE,
        },
      },
      logo: {
        type: 'circle',
        path: 'https://s3.eu-central-1.amazonaws.com/webclient-connector-staging.conversed.biz/assets/conversed_logo.png',
      },
      speechIcon:
        'https://s3.eu-central-1.amazonaws.com/webclient-connector-staging.conversed.biz/assets/conversed_logo.png',
    },
    fonts: {
      chatWindow: DEFAULT_CHAT_WINDOW_FONT_FAMILY,
    },
  },
  scheme: {
    header: {
      headerColor: '#0554e3',
      headerTitleColor: '#FFFFFF',
      headerSubtitleColor: '#FFFFFF',
      windowColor: '#FFFFFF',
      messageBarColor: '#F2F2F2',
    },
    message: {
      messageBubbleColor: '#0554e3',
      messageBubbleTextColor: '#FFFFFF',
      personalMessageBubbleColor: '#F7F7F7',
      personalMessageBubbleTextColor: '#3D3D3D',
      buttonEdgeColor: '#0554e3',
      buttonTextColor: '#0554e3',
      buttonHoverColor: '#90a9ca',
      buttonBackgroundColor: '#FFFFFF',
      carouselBackgroundColor: '#FFFFFF',
      carouselButtonColor: '#0554e3',
      carouselTextColor: '#000000',
      carouselButtonTextColor: '#FFFFFF',
      submitButtonColor: '#0554e3',
      notificationAIGeneratedAnswerTextColor: '#bdbdbd',
      streamingIndicatorColor: '#99a1af',
      urlColor: '#0000EE',
      urlHoverColor: '#0000EE',
      urlClickColor: '#0000EE',
      urlTextDecoration: 'underline',
    },
    greeting: {
      titleColor: '#FFFFFF',
      subtitleColor: '#FFFFFF',
      buttonTextColor: '#FFFFFF',
      buttonColor: '#0554e3',
      backgroundColor: '#FFFFFF',
    },
  },
};

export const getWebclientURL = (
  uuid: string | undefined,
  environment: string | undefined,
  localURL?: string,
): string => {
  if (environment === 'local' && localURL) {
    return `${localURL}/connect.js`;
  }

  switch (environment) {
    case 'develop': {
      return `https://${uuid}-webclient-develop.conversed.biz/connect.js`;
    }
    case 'stage': {
      return `https://${uuid}-webclient-stage.conversed.biz/connect.js`;
    }
    default: {
      return `https://${uuid}-webclient.conversed.biz/connect.js`;
    }
  }
};

const createWebclientConfig = (
  webclient: TWebclient,
  selectedWebclientConfig?: TWebclient['configs'][0],
  config?: GetWebclientConfigDto,
  scriptType = EWebclientScriptType.HEAD,
) => `
<script src='${getWebclientURL(webclient.uuid, webclient.environment)}'></script>
<script${scriptType === EWebclientScriptType.GTM ? '' : ' type="module"'}>
Conversed.init('${webclient.uuid}', ${JSON.stringify(config, null, 2)}, '${
  selectedWebclientConfig?.identifier
}');
</script>
`;

export const createSimpleWebclientSnippet = (
  webclient: TWebclient,
  selectedWebclientConfig?: TWebclient['configs'][0],
  scriptType = EWebclientScriptType.HEAD,
) =>
  createWebclientConfig(
    webclient,
    selectedWebclientConfig,
    {
      botId: webclient.botId,
    },
    scriptType,
  );

export const createAdvancedWebclientSnippet = (
  webclient: TWebclient,
  selectedWebclientConfig?: TWebclient['configs'][0],
  scriptType = EWebclientScriptType.HEAD,
) =>
  createWebclientConfig(
    webclient,
    selectedWebclientConfig,
    {
      botId: webclient.botId,
      ...selectedWebclientConfig?.config,
    },
    scriptType,
  );
